import ConfirmModal from 'components/confirm-modal';
import ToggleSwitch from 'components/toggle-switch';
import { useUser } from 'contexts/UserContext';
import { useTimeOnPageContext } from 'contexts/time-on-page';
import { useEffect, useMemo, useState } from 'react';
import { Button, Tab, Tabs, Form } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import checkMFAEnabled from 'services/mfa/checkMFAEnabled';
import disableMFA from 'services/mfa/disableMFA';
import { ReactComponent as MFAIcon } from 'assets/svg/mfa-icon.svg';
import { AmplitudePageName, track } from 'utils/amplitude';
import SubscriptionSetting from '../../subscription-setting/SubscriptionSetting';
import DataTransfer from '../../views/data-transfer';
import MFAAllSetModal from './MFAAllSetModal';
import styles from './AccountSetting.module.scss';
import getClasses from 'utils/getClasses';
import { ReactComponent as OutlineEdit1 } from 'assets/svg/treatment-plan/outline-edit-1.svg';
import IconButton from 'components/icon-button';
import putOrchidProEmail from 'services/putOrchidProEmail';
import PasswordSettingModal from 'components/auth/PasswordSettingModal';

const ACCOUNT_SETTING_OPTIONS = [
  { value: 'my-account', label: 'My Account' },
  { value: 'subscription-plans', label: 'Subscription Plans' },
  // { value: "data-transfer", label: "Data Transfer" },
  // { value: "notification", label: "Notification" },
];

const AMPLITUDE_PAGE_NAME: AmplitudePageName = 'pro-settings-account';

const AccountSetting = () => {
  const [showPasswordSettingModal, setShowPasswordSettingModal] =
    useState(false);
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const { user } = useUser();
  const navigate = useNavigate();

  const location = useLocation();
  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get('tab') || '',
    };
  }, [location.search]);

  const [tab, setTab] = useState(activeTab || 'my-account');
  const [confirmRemoveMFA, setConfirmRemoveMFA] = useState(false);
  const [error, setError] = useState<any>();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [showEditEmail, setShowEditEmail] = useState(false);

  useEffect(() => {
    if (!user) return;

    checkMFAEnabled().then(res => {
      const { mfa_enabled } = res;
      setMfaEnabled(mfa_enabled);
    });
  }, [user]);

  const handleRemoveMFA = async () => {
    try {
      await disableMFA();
      setMfaEnabled(false);
      setConfirmRemoveMFA(false);
    } catch (error) {
      setError(error);
    }
  };

  const [email, setEmail] = useState(user?.email || '');

  const onSaveEmail = async () => {
    if (!email) return;
    try {
      await putOrchidProEmail(email);
      setShowEditEmail(false);
    } catch (error) {
      setError(error);
    }
  };

  return (
    <div className={styles['account-setting']}>
      <PasswordSettingModal
        show={showPasswordSettingModal}
        onHide={() => setShowPasswordSettingModal(false)}
      />
      {error && (
        <div className={styles['account-setting__error']}>{error.message}</div>
      )}
      <div className={styles['account-setting__header']}>
        <h4 className={'rg-32 deep-blue questrial mb-0'}>Account</h4>
              <Tabs
          activeKey={tab}
          onSelect={k => {
            setTab(s => k || s);
            navigate(`/pro-dashboard/settings/account?tab=${k}`, { replace: true });
          }}
          className={styles['account-setting__tabs']}
        >
          {ACCOUNT_SETTING_OPTIONS.map((el, i) => {
            if (
              user?.role !== 'PRACTICE_SCHEDULER' ||
              el.value !== 'subscription-plans'
            ) {
              return (
                <Tab
                  key={i}
                  eventKey={el.value}
                  title={el.label}
                  tabClassName={styles['account-setting__tab']}
                  onEnter={() =>
                    track(AMPLITUDE_PAGE_NAME, 'account-tab', 'click', {
                      eventProperties: { tab: el.value },
                    })
                  }
                />
              );
            }
            return null;
          })}
        </Tabs>
      </div>

      <div className={styles['account-setting__body']}>
        {tab === 'my-account' && (
          <>
            {confirmRemoveMFA && (
              <ConfirmModal
                labelTitle={'Are you sure?'}
                children={
                  'Do you want to remove 2-Step Verification? This action cannot be undone.'
                }
                labelCancel={'Cancel'}
                labelConfirm={'Yes, Remove'}
                show={confirmRemoveMFA}
                onCancel={() => setConfirmRemoveMFA(false)}
                onConfirm={handleRemoveMFA}
                error={error}
              />
            )}
            <MFAAllSetModal />

            <div className={styles['account-setting__section']}>
              <div className={styles['account-setting__section-title']}>
                Account Information
              </div>
              {!showEditEmail ? (
                <div
                  className={getClasses(
                    'mb-3',
                    styles['account-setting__account-card'],
                  )}
                >
                  <div>
                    Your account is: <b>{email}</b>
                  </div>
                  <IconButton onClick={() => setShowEditEmail(true)}>
                    <OutlineEdit1 />
                  </IconButton>
                </div>
              ) : (
                <div className={styles['account-setting__account-email']}>
                  <div className={styles['account-setting__email-row']}>
                    Account Email:
                    <Form.Group
                      className={styles['account-setting__form-group']}
                      controlId='email'
                    >
                      <Form.Control
                        required
                        type='email'
                        placeholder='Enter Email'
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                      />
                    </Form.Group>
                  </div>
                  <div className={styles['account-setting__account-action']}>
                    <Button
                      variant={'outline-primary'}
                      onClick={() => setShowEditEmail(false)}
                    >
                      Cancel
                    </Button>
                    <Button variant={'primary'} onClick={() => onSaveEmail()}>
                      Save
                    </Button>
                  </div>
                </div>
              )}
              <div className={styles['account-setting__account-card']}>
                <div>Password: **********</div>
                <div>
                  <Button
                    className={'p-0 rg-16 mr-3'}
                    variant={'link'}
                    onClick={() => {
                      setShowPasswordSettingModal(true);
                    }}
                  >
                    Reset Password
                  </Button>
                  <IconButton onClick={() => setShowPasswordSettingModal(true)}>
                    <OutlineEdit1 />
                  </IconButton>
                </div>
              </div>
            </div>
            <div className={styles['account-setting__section']}>
              <div className={styles['account-setting__section-title-mfa']}>
                <MFAIcon />
                2-Step Verification
              </div>
              <div className={styles['account-setting__section-text-mfa']}>
                Secure your account by enabling 2-step verification for an extra
                layer of protection at sign-in.
              </div>
              {!mfaEnabled ? (
                <Button
                  onClick={() => navigate('/pro-dashboard/mfa-setup')}
                >
                  Set up
                </Button>
              ) : (
                <div className={styles['toggle-switch']}>
                  <ToggleSwitch
                    checked={mfaEnabled}
                    onChange={() => {
                      if (mfaEnabled) {
                        setConfirmRemoveMFA(true);
                      } else {
                        window.alert('MFA is disabled');
                      }
                    }}
                  />
                  <div className={styles['toggle-switch-right']}>
                    <div className={styles['toggle-switch-right-title']}>
                      Authentication app
                    </div>
                    <div className={styles['toggle-switch-right-text']}>
                      Sign in using a verification codes from your
                      authentication app
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {tab === 'subscription-plans' && user?.role !== 'PRACTICE_SCHEDULER' && (
          <div className={styles['account-setting__section']}>
            <SubscriptionSetting amplitudePageName={AMPLITUDE_PAGE_NAME} />
          </div>
        )}
        {tab === 'data-transfer' && <DataTransfer />}
      </div>
      <div className={styles['account-setting__footer']}>
        <div className={'gray01 rg-12'}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
      </div>
    </div>
  );
};

export default AccountSetting;
